import { useEffect, useState } from "react";
import { siteConfig } from "@/lib/site-config";

interface FormEmbedProps {
  id?: string;
  className?: string;
  /**
   * Hold the iframe back until the browser is idle. Used for the hero form,
   * which is on screen at first paint but drags in a large third-party bundle
   * that would otherwise compete with the hero image for bandwidth.
   */
  deferUntilIdle?: boolean;
}

/** Native height reported by the embed code — also the reserved placeholder height. */
const FORM_HEIGHT = Number(siteConfig.widgets.formHeight);

/**
 * Embeds the LeadConnector form widget exactly as provided in the
 * business widget code. We never force a height, scroll, or
 * compression — the form_embed.js script controls iframe height.
 */
const FormEmbed = ({
  id = `inline-${siteConfig.widgets.formId}`,
  className = "",
  deferUntilIdle = false,
}: FormEmbedProps) => {
  const [ready, setReady] = useState(!deferUntilIdle);

  useEffect(() => {
    if (ready) return;
    // Whichever comes first: the browser going idle, or the visitor reaching
    // for the form. `requestIdleCallback` is not in Safari, hence the timeout.
    let idleId: number | undefined;
    let timerId: number | undefined;
    const show = () => setReady(true);

    const ric = (window as { requestIdleCallback?: typeof requestIdleCallback })
      .requestIdleCallback;
    if (typeof ric === "function") {
      idleId = ric(show, { timeout: 2500 }) as unknown as number;
    } else {
      timerId = window.setTimeout(show, 1200);
    }
    window.addEventListener("pointerdown", show, { once: true, passive: true });

    return () => {
      const cic = (window as { cancelIdleCallback?: typeof cancelIdleCallback })
        .cancelIdleCallback;
      if (idleId !== undefined && typeof cic === "function") cic(idleId);
      if (timerId !== undefined) clearTimeout(timerId);
      window.removeEventListener("pointerdown", show);
    };
  }, [ready]);

  useEffect(() => {
    if (!ready) return;
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    } else {
      // Script already loaded — re-run any global init if available
      // form_embed.js auto-attaches via postMessage, so simply remounting
      // the iframe is enough for it to start measuring.
      try {
        // @ts-expect-error - global form embed init
        if (typeof window.formEmbedInit === "function") window.formEmbedInit();
      } catch {
        // noop
      }
    }
  }, [ready]);

  return (
    <div className={className}>
      {/* The reserved height lives on this wrapper rather than on the iframe:
          form_embed.js rewrites the iframe's own height while the form loads,
          which collapsed the hero column for several seconds and was the
          single largest layout shift on the page. FORM_HEIGHT is the native
          height the embed code declares via data-height. */}
      <div style={{ minHeight: FORM_HEIGHT }}>
        {ready ? (
          <iframe
            src={siteConfig.widgets.formIframeSrc}
            style={{
              width: "100%",
              minHeight: FORM_HEIGHT,
              border: "none",
              borderRadius: 10,
              display: "block",
            }}
            id={id}
            data-layout="{'id':'INLINE'}"
            data-trigger-type="alwaysShow"
            data-trigger-value=""
            data-activation-type="alwaysActivated"
            data-activation-value=""
            data-deactivation-type="neverDeactivate"
            data-deactivation-value=""
            data-form-name={siteConfig.widgets.formName}
            data-height={siteConfig.widgets.formHeight}
            data-layout-iframe-id={id}
            data-form-id={siteConfig.widgets.formId}
            title={siteConfig.widgets.formName}
            scrolling="no"
          />
        ) : null}
      </div>
    </div>
  );
};

export default FormEmbed;
